export enum PageTab {
  Active = "active",
  Drafts = "drafts",
  Archived = "archived",
}

const pageTabs = Object.values(PageTab) as string[];

export function isPageTab(value: string | null | undefined): value is PageTab {
  return value !== null && value !== undefined && pageTabs.includes(value);
}

/**
 * How full the two working tabs are, as the count queries answer them.
 *
 * `undefined` is "not answered yet", which is not the same as zero: an empty tab and an uncounted
 * one lead to opposite decisions, so they must not collapse into one value.
 */
export type ProposalTabCounts = {
  /** Every active proposal, whichever party owes the next move. */
  active: number | undefined;
  /**
   * Every draft in this user's draft box. The draft box has no half that is waiting on the other
   * party -- the server splits it into drafts they are to submit and drafts they were asked to
   * read, and nothing else -- so this count is already "drafts waiting on you".
   */
  drafts: number | undefined;
  /**
   * The active proposals whose next move is this user's: the Inbox box, which is the one count the
   * API describes as "waiting on you" rather than "in flight".
   *
   * Only the tie-break below reads it, so a caller whose tabs are not tied may leave it unanswered
   * rather than spend a request on it.
   */
  activeWaitingOnYou: number | undefined;
};

/**
 * The tab to open when nothing else names one, or `undefined` while a count it needs is still on
 * the wire.
 *
 * A user whose proposals are all drafts -- or all active -- was landing on the other tab's empty
 * table and having to click to find their work (#5245). So the page opens the tab that has
 * something in it, and when both do, the one that has something waiting on this reader.
 *
 * Returning `undefined` means "not yet", never "Active". A caller must wait on it rather than open
 * a tab it will then take back: a table that redraws under the reader is worse than a moment of
 * skeletons, and on this page the tab it opened is also a list request it has to pay for twice
 * (#5131).
 *
 * Archived is never the answer. It is a tab of work that is over, so a reader with nothing live has
 * nothing to do there, and the `Active` fallback is also what the page has always opened on.
 */
export function openingPageTab(counts: ProposalTabCounts): PageTab | undefined {
  const { active, drafts, activeWaitingOnYou } = counts;
  if (active === undefined || drafts === undefined) return undefined;

  // Exactly one tab with rows decides it, and two empty tabs leave nothing to decide.
  if (drafts === 0) return PageTab.Active;
  if (active === 0) return PageTab.Drafts;

  // Both tabs have rows, so "which one has work in it" no longer tells them apart. What does is
  // which one has work waiting on this reader, and the two tabs answer that differently: every
  // draft in the draft box is this user's side to move, while the active tab also lists the
  // proposals sitting with the counterparty. So the active tab earns the opening only when its
  // inbox is not empty.
  if (activeWaitingOnYou === undefined) return undefined;
  return activeWaitingOnYou > 0 ? PageTab.Active : PageTab.Drafts;
}

/**
 * The key an explicit tab choice is remembered under.
 *
 * `session.ts` clears it on sign-out by name, the way it clears `communications.activeTab`.
 */
export const PAGE_TAB_STORAGE_KEY = "work.proposals.activeTab";

/**
 * The tab the user last chose by clicking one, if they have chosen in this browser session.
 *
 * A reader who has said which tab they want keeps it for the rest of the session: the counts are
 * how the page guesses, and a guess does not get to overrule an answer. It is deliberately not
 * written by merely arriving on a tab -- the opening tab this module picks is arrived on too, and
 * remembering that would switch the guess off after the first page view.
 */
export function readStoredPageTab(): PageTab | undefined {
  try {
    const storedTab = globalThis.sessionStorage?.getItem(PAGE_TAB_STORAGE_KEY);
    return isPageTab(storedTab) ? storedTab : undefined;
  } catch {
    return undefined;
  }
}

export function storePageTab(tab: PageTab): void {
  try {
    globalThis.sessionStorage?.setItem(PAGE_TAB_STORAGE_KEY, tab);
  } catch {
    // Ignore storage failures; the counts still pick an opening tab on the next visit.
  }
}
